import React, { useEffect, useRef } from 'react';
import ContactForm from '../components/ContactForm';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaClock } from 'react-icons/fa';

// Estilos de Snazzy Maps - Subtle Grayscale (fuera del componente para evitar re-renders)
const mapStyles = [
  { "featureType": "water", "elementType": "geometry", "stylers": [{ "color": "#e9e9e9" }, { "lightness": 17 }] },
  { "featureType": "landscape", "elementType": "geometry", "stylers": [{ "color": "#f5f5f5" }, { "lightness": 20 }] },
  { "featureType": "road.highway", "elementType": "geometry.fill", "stylers": [{ "color": "#ffffff" }, { "lightness": 17 }] },
  { "featureType": "road.highway", "elementType": "geometry.stroke", "stylers": [{ "color": "#ffffff" }, { "lightness": 29 }, { "weight": 0.2 }] },
  { "featureType": "road.arterial", "elementType": "geometry", "stylers": [{ "color": "#ffffff" }, { "lightness": 18 }] },
  { "featureType": "road.local", "elementType": "geometry", "stylers": [{ "color": "#ffffff" }, { "lightness": 16 }] },
  { "featureType": "poi", "elementType": "geometry", "stylers": [{ "color": "#f5f5f5" }, { "lightness": 21 }] },
  { "featureType": "poi.park", "elementType": "geometry", "stylers": [{ "color": "#dedede" }, { "lightness": 21 }] },
  { "elementType": "labels.text.stroke", "stylers": [{ "visibility": "on" }, { "color": "#ffffff" }, { "lightness": 16 }] },
  { "elementType": "labels.text.fill", "stylers": [{ "saturation": 36 }, { "color": "#333333" }, { "lightness": 40 }] },
  { "elementType": "labels.icon", "stylers": [{ "visibility": "off" }] },
  { "featureType": "transit", "elementType": "geometry", "stylers": [{ "color": "#f2f2f2" }, { "lightness": 19 }] },
  { "featureType": "administrative", "elementType": "geometry.fill", "stylers": [{ "color": "#fefefe" }, { "lightness": 20 }] },
  { "featureType": "administrative", "elementType": "geometry.stroke", "stylers": [{ "color": "#fefefe" }, { "lightness": 17 }, { "weight": 1.2 }] }
];

const Contact = () => {
  const { t } = useTranslation();
  const mapRef = useRef(null);

  useEffect(() => {
    // Cargar Google Maps API
    const loadGoogleMaps = () => {
      if (window.google && window.google.maps) {
        initMap();
        return;
      }

      const script = document.createElement('script');
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=initMapCallback`;
      script.async = true;
      script.defer = true;

      window.initMapCallback = initMap;
      document.head.appendChild(script);
    };

    const initMap = () => {
      if (!mapRef.current || !window.google) return;

      const location = { lat: -34.7952665, lng: -55.9376641 }; // Ciudad de la Costa

      const map = new window.google.maps.Map(mapRef.current, {
        center: location,
        zoom: 16,
        styles: mapStyles,
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true
      });

      // Marcador personalizado
      const marker = new window.google.maps.Marker({
        position: location,
        map: map,
        title: 'Exhibirte Uruguay - 18 De Julio Y Av.Eduardo Perez, Ciudad de la Costa',
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#1E5096',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 3
        }
      });

      marker.addListener('click', () => {
        window.open('https://www.google.com/maps/place/18+De+Julio+%26+Avenida+Eduardo+Perez+Robido,+15800+Ciudad+de+la+Costa,+Departamento+de+Canelones/@-34.7952665,-55.9376641,17z', '_blank');
      });
    };

    loadGoogleMaps();
  }, []);

  return (
    <div id="contact-page">
      {/* Hero Section */}
      <section className="py-5 text-white" style={{
        background: 'linear-gradient(135deg, rgba(30, 80, 150, 0.9) 0%, rgba(22, 61, 115, 0.9) 100%), url("/stand-jefo.png") center/cover no-repeat'
      }}>
        <Container>
          <Row className="align-items-center">
            <Col lg={8} className="mx-auto text-center">
              <h1 className="display-5 fw-bold mb-2">{t('contact.subtitle')}</h1>
            </Col>
          </Row>
        </Container>
      </section>


      {/* Contact Info Section */}
      <section className="py-5" style={{ background: '#f8f9fa' }}>
        <Container>
          <Row className="justify-content-center text-center">
            <Col md={3} className="mb-4">
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="p-4">
                  <div className="mb-3">
                    <FaMapMarkerAlt size={32} color="#1E5096" />
                  </div>
                  <h5 style={{ color: '#1E5096' }}>{t('contact.address')}</h5>
                  <p className="text-muted mb-0">{t('contact.addressValue')}</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} className="mb-4">
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="p-4">
                  <div className="mb-3">
                    <FaPhoneAlt size={32} color="#1E5096" />
                  </div>
                  <h5 style={{ color: '#1E5096' }}>{t('contact.phone')}</h5>
                  <p className="text-muted mb-0">{t('contact.phoneValue')}</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} className="mb-4">
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="p-4">
                  <div className="mb-3">
                    <FaEnvelope size={32} color="#1E5096" />
                  </div>
                  <h5 style={{ color: '#1E5096' }}>{t('contact.email')}</h5>
                  <p className="text-muted mb-0">{t('contact.emailValue')}</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} className="mb-4">
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="p-4">
                  <div className="mb-3">
                    <FaClock size={32} color="#1E5096" />
                  </div>
                  <h5 style={{ color: '#1E5096' }}>{t('contact.hours')}</h5>
                  <p className="text-muted mb-0">{t('contact.hoursValue')}</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Contact Form Section */}
      <section className="py-5 contact-section-bg">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8}>
              <ContactForm />
            </Col>
          </Row>
        </Container>
      </section>

      {/* Map Section */}
      <section className="py-0">
        <div
          ref={mapRef}
          style={{
            width: '100%',
            height: '450px',
            display: 'block'
          }}
        />
      </section>
    </div>
  );
}

export default Contact;
